import { Button, Dialog } from "@app/ui";
import { useEffect, useState } from "react";
import { useI18n } from "../../lib/useI18n.js";
import { useCalendarsStore, useIcsStore } from "../../stores/index.js";

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Pick which calendars to include before exporting to .ics. */
export function IcsExportDialog({ open, onClose }: Props) {
  const { t } = useI18n();
  const calendars = useCalendarsStore((s) => s.calendars);
  const exportIcs = useIcsStore((s) => s.exportIcs);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) setSelected(new Set(calendars.map((c) => c.id)));
  }, [open, calendars]);

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allSelected = selected.size === calendars.length;

  return (
    <Dialog open={open} onClose={onClose} title={t("ics.exportTitle")} className="max-w-md">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">{t("ics.exportDescription")}</p>

        <div className="flex flex-col gap-1">
          <button
            type="button"
            className="self-start text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setSelected(allSelected ? new Set() : new Set(calendars.map((c) => c.id)))}
          >
            {allSelected ? t("common.clearAll") : t("common.selectAll")}
          </button>
          {calendars.map((c) => (
            <label key={c.id} className="flex items-center gap-2 rounded-md px-1 py-1 text-sm">
              <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
              {c.color ? <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} /> : null}
              <span>{c.name}</span>
            </label>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            variant="primary"
            disabled={selected.size === 0}
            onClick={() => { onClose(); void exportIcs([...selected]); }}
          >
            {t("ics.exportButton", {
              count: selected.size,
              noun: selected.size === 1 ? t("common.calendar") : t("common.calendars"),
            })}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
